import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { BulbOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import { AppStoreType, ReleaseStepType } from '../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppStoreType, selectIsReleaseFromScraper } from '../../selectors/selectors';
import { getIsAppleAppStore } from '../../helpers';
import {
    displayStoreNameForAppStoreType,
    phasedReleaseDisplayTextForAppStoreType,
    displayCIText,
} from '../../helpers/displayHelpers';

interface EmptyStepBannerProps {
    releaseStepType: ReleaseStepType;
    customMessage?: string;
    appId: string;
    releaseId: string;
    isWorkflowsCIStepEnabled: boolean;
}

const getDescriptionForStep = (releaseStepType: ReleaseStepType, appStoreType: AppStoreType, isWorkflows = true) => {
    switch (releaseStepType) {
        case ReleaseStepType.Kickoff:
            return `The Kickoff step serves as an overview for code and branch-related details, as well as pilot and planning information that may be helpful as your release process is getting started.`;
        case ReleaseStepType.FeatureReadiness:
            return `The Feature readiness step surfaces all work going into the release, as commits or pull requests and their associated tickets in your project management platform.`;
        case ReleaseStepType.ReleaseCandidate:
            return `The ${displayCIText(isWorkflows, 'capitalize')} step shows the build status of your latest ${displayCIText(isWorkflows, 'lowercase')} builds for the release.`;
        case ReleaseStepType.Metadata:
            return `The Metadata step surfaces a number of key metadata fields, including What's new / Release notes, and ${
                getIsAppleAppStore(appStoreType) ? 'Keywords' : "your app's Short description"
            }. You can edit metadata from here, and approve it for release once it's ready.`;
        case ReleaseStepType.RegressionTesting:
            return `The Regression testing step surfaces the status of manual regression testing, and allows you to create regression testing items to perform every release. You can also connect a regression testing integration to surface the status of tests as they exist in your testing platform.`;
        case ReleaseStepType.Approvals:
            return `The Approvals step can be used to manage stakeholder approvals for the release. You can create approval items, and assign them to specific individuals or users with specific groups.`;
        case ReleaseStepType.BetaTesting:
            return `The Beta testing step tracks the status of your chosen beta soak period, and surface the latest beta testing builds available along with their testing status, testing groups, and beta release notes.`;
        case ReleaseStepType.Screenshots:
            return `The Screenshots step surfaces your app's latest app store screenshots – from here you can add and remove screenshot assets, and approve the latest screenshots for release.`;
        case ReleaseStepType.Submission:
            return `The App submission step surfaces the ${displayStoreNameForAppStoreType(
                appStoreType,
            )} submission status of your release. You can select a build for submission from here, and either manually submit it for review or have Runway automatically submit on your behalf based on your preconfigured conditions.`;
        case ReleaseStepType.StoreReview:
            return `The ${displayStoreNameForAppStoreType(
                appStoreType,
            )} review step surfaces the latest review status of your release.`;
        case ReleaseStepType.Takeoff:
            return `The Release step surfaces the status of your released build – you can view the ${phasedReleaseDisplayTextForAppStoreType(
                appStoreType,
            )} status and progress, and with the build's latest stability metrics and recent issues.`;
    }
    return '';
};

export const EmptyStepBanner: React.FC<EmptyStepBannerProps> = ({
    releaseStepType,
    appId,
    customMessage,
    releaseId,
    isWorkflowsCIStepEnabled: isWorkflows,
}) => {
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));

    if (!isReleaseFromScraper) {
        return null;
    }

    return (
        <div
            style={{
                backgroundColor: antGrey.grey3,
                padding: '24px 36px',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    gap: 12,
                }}
            >
                <BulbOutlined
                    style={{
                        fontSize: 20,
                        color: runwayColors.primary,
                    }}
                />
                <Space direction="vertical" size={8}>
                    <Text style={{ color: antGrey.grey8 }}>
                        {customMessage ?? getDescriptionForStep(releaseStepType, appStoreType, isWorkflows)}{' '}
                        <strong>Mouse over a feature to learn more.</strong>
                    </Text>
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        You’re seeing this message because the current version wasn’t released with Runway.
                    </Text>
                </Space>
            </div>
        </div>
    );
};
